import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Card,
  EmptyState,
  PageBody,
  SectionLabel,
  Spinner,
  cn,
} from '@wundo/ui';
import type { GenAiMessage, GenAiMessagePart } from '@wundo/shared/toGenAiMessages.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';
import { fetchTrace, type TraceCall, type TraceContentFilter } from '@/lib/traces.ts';

export const Route = createFileRoute('/_app/traces/$traceId')({
  staticData: { orgScope: 'none' },
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: TraceRoute,
});

function TraceRoute() {
  return <TracePage traceId={Route.useParams().traceId} />;
}

/* Prose keeps the text face; JSON keeps the monospace one. */
const TEXT_CLASS = 'text-sm leading-6 break-words whitespace-pre-wrap text-ink-body';
const PRE_CLASS =
  'overflow-x-auto text-xs leading-5 break-words whitespace-pre-wrap text-ink-body';

/**
 * Every LLM call made under one trace id, in the order they were made: what
 * the model was sent, which tools it could use, and what it answered.
 */
export function TracePage({ traceId }: { traceId: string }) {
  const trace = useQuery({
    queryKey: ['trace', traceId],
    queryFn: () => fetchTrace(traceId),
  });

  if (trace.error) throw trace.error;

  const calls = trace.data?.calls ?? [];

  return (
    <>
      <PageHeader
        title='Trace'
        subtitle={
          <span className='font-mono text-sm'>
            {traceId}
            {trace.data && ` · ${calls.length} ${calls.length === 1 ? 'call' : 'calls'}`}
          </span>
        }
      />
      <PageBody className='mt-3.5 flex flex-col gap-5'>
        {trace.isPending && <Spinner className='size-5' />}

        {trace.data && calls.length === 0 && (
          <Card>
            <EmptyState
              title='No LLM calls in this trace'
              description='Calls are kept for 30 days.'
            />
          </Card>
        )}

        {calls.map((call, index) => (
          <CallCard key={call.id} call={call} index={index} />
        ))}
      </PageBody>
    </>
  );
}

function CallCard({ call, index }: { call: TraceCall; index: number }) {
  const system = call.input.filter((message) => message.role === 'system');
  const conversation = call.input.filter((message) => message.role !== 'system');
  const tools = Array.isArray(call.tool_definitions) ? call.tool_definitions : [];

  return (
    <Card as='section' aria-label={`Call ${index + 1}`}>
      <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
        <h2 className='text-md font-bold text-ink'>
          {index + 1}. {call.name}
        </h2>
        <p className='text-sm text-ink-muted'>
          {call.model}
          <span className='px-1.5'>·</span>
          {new Date(call.created_at).toLocaleString()}
        </p>
      </div>

      {(system.length > 0 || tools.length > 0) && (
        <Accordion className='mt-3.5 gap-2'>
          {system.length > 0 && (
            <Folded label='System prompt'>
              {system.map((message, i) => (
                <MessageParts key={i} parts={message.parts} />
              ))}
            </Folded>
          )}
          {tools.length > 0 && (
            <Folded label={`Tools (${tools.length})`}>
              <pre className={PRE_CLASS}>{JSON.stringify(tools, null, 2)}</pre>
            </Folded>
          )}
        </Accordion>
      )}

      <SectionLabel as='h3' className='mt-5'>
        Messages
      </SectionLabel>
      <div className='mt-2.5 flex max-h-160 flex-col gap-2.5 overflow-y-auto pr-1'>
        {conversation.map((message, i) => (
          <MessageBlock key={i} message={message} />
        ))}
      </div>

      <SectionLabel as='h3' className='mt-5'>
        Output
      </SectionLabel>
      <div className='mt-2.5 flex flex-col gap-2.5'>
        {call.error && (
          <div
            role='alert'
            className='rounded-md border border-danger-line bg-danger-fill px-3.5 py-2.5'
          >
            <p className='mb-1 text-xs font-bold tracking-widest text-danger uppercase'>
              Failed
            </p>
            <p className={cn(TEXT_CLASS, 'text-danger')}>{call.error}</p>
          </div>
        )}
        {call.content_filters?.map((filter, i) => (
          <ContentFilterNote key={i} filter={filter} />
        ))}
        {call.output?.map((message, i) => (
          <MessageBlock key={i} message={message} tone='output' />
        ))}
        {!call.error && !call.output?.length && (
          <p className='text-sm text-ink-muted'>Nothing</p>
        )}
      </div>
    </Card>
  );
}

function ContentFilterNote({ filter }: { filter: TraceContentFilter }) {
  const categories = Object.entries(filter.categories)
    .map(([category, severity]) => `${category} (${severity})`)
    .join(', ');
  return (
    <p className={cn('text-sm', filter.blocked ? 'text-danger' : 'text-ink-muted')}>
      Content filter {filter.blocked ? 'blocked' : 'flagged'} the {filter.source}
      {categories && `: ${categories}`}
    </p>
  );
}

function Folded({ label, children }: { label: string; children: ReactNode }) {
  return (
    <AccordionItem className='bg-fill-faint'>
      <AccordionTrigger className='px-3.5 py-2.5 text-sm font-normal text-ink-secondary'>
        {label}
      </AccordionTrigger>
      <AccordionPanel>
        <div className='flex max-h-160 flex-col gap-2.5 overflow-y-auto px-3.5 pb-3.5'>
          {children}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
}

function MessageBlock({
  message,
  tone = 'input',
}: {
  message: GenAiMessage;
  tone?: 'input' | 'output';
}) {
  return (
    <div
      className={
        tone === 'output'
          ? 'rounded-md bg-accent-tint px-3.5 py-2.5'
          : 'rounded-md border border-line px-3.5 py-2.5'
      }
    >
      <p className='mb-1 text-xs font-bold tracking-widest text-ink-muted uppercase'>
        {message.role}
      </p>
      <MessageParts parts={message.parts} />
    </div>
  );
}

function MessageParts({ parts }: { parts: GenAiMessagePart[] }) {
  return (
    <div className='flex flex-col gap-2'>
      {parts.map((part, i) => (
        <Part key={i} part={part} />
      ))}
    </div>
  );
}

function Part({ part }: { part: GenAiMessagePart }) {
  switch (part.type) {
    case 'text':
      return <p className={TEXT_CLASS}>{part.content}</p>;
    case 'reasoning':
      return <p className={cn(TEXT_CLASS, 'italic text-ink-muted')}>{part.content}</p>;
    case 'tool_call':
      return (
        <div className='rounded-sm bg-fill-faint p-2'>
          <p className='text-xs font-bold text-ink-secondary'>Calls {part.name}</p>
          <pre className={PRE_CLASS}>{formatArguments(part.arguments)}</pre>
        </div>
      );
    case 'tool_call_response':
      return (
        <div className='rounded-sm bg-fill-faint p-2'>
          <p className='text-xs font-bold text-ink-secondary'>
            Result{part.name ? ` of ${part.name}` : ''}
          </p>
          <pre className={PRE_CLASS}>{formatResult(part.content)}</pre>
        </div>
      );
  }
}

function formatArguments(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

/* A tool result is often JSON in a string; indented it can be read. */
function formatResult(content: string): string {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
}
